import type { Command } from 'commander';
import { unwrapDataArray } from '@wport/core';
import { enterpriseGet } from '../../../lib/enterprise-client';
import { resolveApiKey } from '../../../lib/credentials-store';
import { resolveContext } from '../../../lib/global-opts';
import { dim, printJson, printTable, type OutputFormat } from '../../../lib/output';

/** 欄位對齊 server EnterpriseKeyVm（enterprise-key.vm.ts，pm_41 KEY-M-1）。明文不在此。 */
export interface EnterpriseKeyItem {
	enc_id?: string;
	name?: string | null;
	key_prefix?: string;
	key_last4?: string | null;
	scopes?: string[];
	status?: 'active' | 'expired' | 'revoked' | string;
	expires_at?: string | null;
	last_used_at?: string | null;
	[k: string]: unknown;
}

function formatDate(value: string | null | undefined): string {
	return value ? String(value).slice(0, 10) : '';
}

function formatScopes(scopes: string[] | undefined): string {
	return Array.isArray(scopes) ? scopes.join(',') : '';
}

/**
 * `keys list` 核心：驗 key → GET /keys → 輸出。與 commander 註冊分離，方便測試
 * （同 login.performLogin 模式）。GET /keys 回 DataResponse（非分頁），data 是陣列。
 */
export async function runKeysList(
	ctx: { baseUrl: string; locale: string; timeoutMs: number; format: OutputFormat; color: boolean },
	apiKey: string
): Promise<void> {
	const { body } = await enterpriseGet(
		{ baseUrl: ctx.baseUrl, locale: ctx.locale, timeoutMs: ctx.timeoutMs, apiKey },
		'/keys'
	);
	const keys = unwrapDataArray<EnterpriseKeyItem>(body);

	if (ctx.format === 'json') {
		printJson(keys);
		return;
	}

	printTable(
		keys,
		[
			{ header: 'ENC_ID', value: (r) => (r.enc_id ?? '').slice(0, 14) },
			{ header: 'NAME', value: (r) => r.name ?? '', maxWidth: 24 },
			{ header: 'LAST4', value: (r) => r.key_last4 ?? '' },
			{ header: 'SCOPES', value: (r) => formatScopes(r.scopes), maxWidth: 28 },
			{ header: 'STATUS', value: (r) => r.status ?? '' },
			{ header: 'EXPIRES', value: (r) => formatDate(r.expires_at), maxWidth: 12 },
			{ header: 'LAST_USED', value: (r) => formatDate(r.last_used_at), maxWidth: 12 },
		],
		ctx.color
	);
	const active = keys.filter((k) => k.status === 'active').length;
	process.stdout.write(dim(`${keys.length} key(s), ${active} active.`, ctx.color) + '\n');
}

export function registerEnterpriseKeysList(parent: Command): void {
	parent
		.command('list')
		.description('List your company API keys (masked)')
		.action(async (_flags: unknown, command: Command) => {
			const ctx = resolveContext(command);
			const globals = command.optsWithGlobals() as { apiKey?: string };
			const { key } = resolveApiKey(globals.apiKey);
			await runKeysList(ctx, key);
		});
}

// Internal helpers exposed for tests. Not part of the public CLI API.
export const __test__ = { formatDate, formatScopes };
